import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { catchError, concatMap, EMPTY, Observable, switchMap } from 'rxjs';
import { UserConfigsService } from '../../../../services/user-configs.service';
import { OkCancelDialogComponent } from '../../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { OkCancelData } from '../../../dialogs/ok-cancel/ok-cancel-dialog.model';
import { Role } from '../../../../models/role.model';
import { CaseManager } from '../../../../managers/case.manager';
import { CaseAccessManager } from '../../../../managers/case-access.manager';
import { NotificationManager } from '../../../../managers/notification.manager';
import { LexbeCase } from '../../../../models/case.model';
import { CaseConfigComponent } from '../case-config/case-config.component';
import { ManageCasePermissionDialogComponent, ManageCasePermissionDialogData } from '../manage-case-permission-dialog/manage-case-permission-dialog.component';
import { CaseResource } from '../../../../models/resource/case.resource';
import { AccountAdminService } from '../../../../services/account-admin.service';
import { AuthenticationService } from '../../../../services/authentication.service';
import { CaseConfigService } from '../../../../services/case-config.service';
import { CaseConfig } from '../../../../models/case-config.model';
import { BaseComponent } from '../../../base.component';
import { CasesService } from '../../../../services/cases.service';
import { CaseAdminSdk } from '../../../../sdks/case-admin.sdk';
import { PatchDeltaObjectUtility } from '../../../../utilities/patch-delta-object.utility';
import { CaseShards } from '../../../../models/case-shards.model';
import { ReshardCaseIndexDialogComponent } from '../reshard-case-index-dialog/reshard-case-index-dialog.component';
import { CaseConfigTypeId } from '../../../../models/case-config-type.enum';
import { IndexClient } from "@clients/api.client";

@Component({
	selector: 'lex-case-edit',
	templateUrl: './case-edit.component.html',
	styleUrls: ['./case-edit.component.scss'],
	standalone: false
})
export class CaseEditComponent extends BaseComponent implements OnChanges {

	@Input() selectedCaseId: string = '';
	@Input() searchTerm: string = '';
	@Input() selectedOrderBy: [string, "ASC" | "DESC"] = ['CaseName', 'ASC'];

	public selectedCase: LexbeCase;
	public selectedCaseShard: CaseShards;
	public caseForm: UntypedFormGroup;
	public caseRefreshed: boolean = false;
	public caseShardsRefreshed: boolean = false;

	private useGigabytes: boolean = false;
	private constBytesPerGibibytes = 1073741824;
	private constBytesPerGigabytes = 1000000000;

	constructor(
		private formBuilder: UntypedFormBuilder,
		private router: Router,
		private caseManager: CaseManager,
		private caseAccessManager: CaseAccessManager,
		private notificationManager: NotificationManager,
		private userConfigsService: UserConfigsService,
		private caseConfigService: CaseConfigService,
		private casesService: CasesService,
		private accountAdminService: AccountAdminService,
		private caseAdminSdk: CaseAdminSdk,
		private authenticationService: AuthenticationService,
		private dialog: MatDialog,
		private indexClient: IndexClient) {
		super();
		this.caseForm = this.formBuilder.group({
			caseName: ['', [Validators.required, Validators.maxLength(256)]],
			simpleCaseId: ['', [Validators.required, Validators.maxLength(50)]],
			caseComments: ['']
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.selectedCaseId && this.selectedCaseId !== this.selectedCase?.caseId) {
			this.caseRefreshed = false;
			this.resetForm(true);
			this.caseShardsRefreshed = false;
			this.caseConfigService.getCaseConfigs(this.selectedCaseId).pipe(switchMap((data) => {
				this.useGigabytes = JSON.parse(data.filter((config: CaseConfig) => config.configTypeId === CaseConfigTypeId.UseGigabytesCase)[0].value);
				if (this.userConfigsService.inRoles(Role.Administrator))
					return this.caseManager.getCaseShards(this.selectedCaseId);
				return EMPTY;
			})).subscribe(data => {
				if (data) {
					this.selectedCaseShard = data;
					this.caseShardsRefreshed = true;
				}
			});
			this.caseManager.getCase(this.selectedCaseId).subscribe(data => {
				this.selectedCase = data;
				this.resetForm();
				this.caseRefreshed = true;
			})
		}
	}

	public canEditCase(): boolean {
		return this.userConfigsService.inRoles(Role.GlobalAccountAdvancedFeatureSet, Role.Administrator, Role.ProfessionalServices);
	}

	public convertToGB(size: number): string {
		if (this.useGigabytes)
			return (size / this.constBytesPerGigabytes).toFixed(2);
		else
			return (size / this.constBytesPerGibibytes).toFixed(2);
	}

	public accessCase(): void {
		this.caseAdminSdk.updateCaseUserAccess(this.selectedCase.caseId, this.userConfigsService.currentConfig.userId)
			.pipe(
				concatMap((caseId) => this.casesService.setCurrentCase(caseId)),
				concatMap(() => this.authenticationService.refreshToken()))
			.subscribe(() => {
				this.router.navigate(['review/browse']);
			});
	}

	public synchronizeCaseIndex(): void {
		this.synchronizeCaseIndexDialog().subscribe(() => {
			this.notificationManager.displaySnackBarMessage({
				message: `Case synchronization requested`,
				ttl: 5000
			});
		}, () => {
			this.notificationManager.displaySnackBarMessage({
				message: `Failed. Please try again.`,
				type: 'error',
				ttl: 5000
			});
		});
	}

	public reshardCaseIndex(): void {
		this.reshardCaseIndexDialog().subscribe((data) => {
			this.selectedCaseShard = data;
			this.caseShardsRefreshed = true;
			this.notificationManager.displaySnackBarMessage({
				message: 'Reshard case index job queued successfully.'
			});
		}, err => {
			this.notificationManager.displaySnackBarMessage({
				message: `Failed to queue re-shard case index - ${err}`,
				type: 'error'
			});
		});
	}

	public configCase(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = this.selectedCase;
		this.dialog.open(CaseConfigComponent, dialogConfig);
	}

	public manageUserAccess(): void {
		this.manageCasePermissionDialog(this.selectedCase.accountId, this.selectedCase)
			.pipe(
				switchMap(() => this.caseManager.getCase(this.selectedCaseId)),
				switchMap((lexbeCase) => {
					this.selectedCase = lexbeCase;
					this.notificationManager.displaySnackBarMessage({
						message: 'Case permission updated successfully.'
					});
					return EMPTY;
				}), catchError((e) => {
					this.notificationManager.displaySnackBarMessage({
						message: `Failed to update case permission- ${e?.error?.error?.message}`,
						type: 'error'
					});
					return EMPTY;
				})).subscribe();
	}

	public delete(): void {
		this.deleteCaseDialog(this.selectedCase.caseId).subscribe(() => {
			this.caseManager.refreshAdminCases(this.searchTerm, this.selectedOrderBy);
			this.notificationManager.displaySnackBarMessage({
				message: 'Case deleted successfully.'
			});
		}, err => {
			this.notificationManager.displaySnackBarMessage({
				message: `Failed to delete case - ${err}`,
				type: 'error'
			});
		});
	}

	public update(): void {
		if (!this.caseForm.valid)
			return;

		const jobPatch = PatchDeltaObjectUtility.getDeltaObject<CaseResource>(this.caseForm.controls);

		if (jobPatch === null)
			return;

		this.caseManager.updateCase(this.selectedCase.caseId, jobPatch).pipe(
			switchMap(() => {
				return this.caseManager.getCase(this.selectedCaseId);
			})).subscribe((lexbeCase) => {
				this.selectedCase = lexbeCase;
				this.resetForm();
				this.notificationManager.displaySnackBarMessage({
					message: 'Case updated successfully.'
				});
			}, err => {
				this.notificationManager.displaySnackBarMessage({
					message: `Failed to update case- ${err}`, type: 'error'
				});
				this.resetForm();
			});
	}

	public cancel(): void {
		this.resetForm();
	}

	//#region Dialogs for Admin Cases specific functionalities

	private synchronizeCaseIndexDialog(): Observable<any> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';

		dialogConfig.data = {
			title: `Index Case Confirmation`,
			message: `This will request case and index synchronization.`
		} as OkCancelData;

		return this.dialog.open(OkCancelDialogComponent, dialogConfig)
			.afterClosed()
			.pipe(
				switchMap((confirm) => {
					if (confirm) {
						return this.indexClient.synchronizeCaseIndex$(this.selectedCaseId);
					}
					return EMPTY;
				})
			);
	}

	private reshardCaseIndexDialog(): Observable<CaseShards> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = { selectedCase: this.selectedCase, currentShardCount: this.selectedCaseShard.primaryShardCount, currentShardSize: this.convertToGB(this.selectedCaseShard.avgPrimaryBytes) };
		return this.dialog.open(ReshardCaseIndexDialogComponent, dialogConfig)
			.afterClosed()
			.pipe(
				switchMap((success) => {
					if (success) {
						this.caseShardsRefreshed = false;
						return this.caseManager.getCaseShards(this.selectedCase.caseId);
					}
					return EMPTY;
				})
			);
	}

	private manageCasePermissionDialog(accountId: string, lexbeCase: LexbeCase, isNewCase?: boolean): Observable<string> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = { selectedAccountId: accountId, selectedCase: lexbeCase, isNewCase: isNewCase } as ManageCasePermissionDialogData;
		return this.dialog.open(ManageCasePermissionDialogComponent, dialogConfig).afterClosed()
			.pipe(switchMap((userIds) => {
				if (userIds)
					return this.caseAccessManager.updateCaseUsersAccessesWithEmployees(lexbeCase.caseId, userIds);
				else
					return EMPTY;
			}));
	}

	private deleteCaseDialog(caseId: string): Observable<void> {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';

		dialogConfig.data = {
			title: `Delete Case Confirmation`,
			message: `The user will no longer have access to this case.`,
			showAcknowledge: true
		} as OkCancelData;

		return this.dialog.open(OkCancelDialogComponent, dialogConfig)
			.afterClosed()
			.pipe(
				switchMap((confirm) => {
					if (confirm)
						return this.caseManager.deleteCase(caseId);
					return EMPTY;
				})
			);
	}

	//#endregion Dialogs for Admin Cases specific functionalities

	private resetForm(clear: boolean = false): void {
		if (this.selectedCase && !clear)
			this.caseForm.reset({
				caseName: { value: this.selectedCase.caseName, disabled: !this.canEditCase() },
				simpleCaseId: { value: this.selectedCase.simpleCaseId, disabled: !this.canEditCase() },
				caseComments: { value: this.selectedCase.caseComments, disabled: !this.canEditCase() }
			});
		else {
			this.caseForm.reset({
				caseName: { value: '', disabled: false },
				simpleCaseId: { value: '', disabled: false },
				caseComments: { value: '', disabled: false }
			});
		}
	}

}
